import React, { useContext, useEffect, useState } from "react";
import { FormPage, TabPanel, ToastType, notifyToast, AppDynamicForm, FormButtonToolbar } from "../../components-old/ComponentsIndex";
import { useSelector } from "react-redux";
import { selectSessionConfig, selectUser } from "../../store/store-selector";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { DisplaySpinnerContext } from "../../context/ContextIndex";
import { handlePostAction } from "../../utils/event-api-invoke";
import { UserRoleFormData, UserRoleFormErrorData, setUserRoleFormFieldContent } from "./CreateUserRoleFormData";
import { useSearchParams, useLocation } from "react-router-dom";
import { AppUtility } from "../../utils/utility";
import _ from "lodash";

const CreateUserRoleForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const EditUserEmail = searchParams.get("userEmail");
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);
  const navigationData = location.state;
  const user = useSelector(selectUser);

  /**************************************************
  //Form state variables
  **************************************************/
  const [selectedTab, setSelectedTab] = useState("0");
  const [formStateData, setFormStateData] = useState({
    formData: _.cloneDeep(UserRoleFormData),
    formError: _.cloneDeep(UserRoleFormErrorData),
    disableFormSubmit: true,
    validated: false,
  });

  /**************************************************
  //Event definitions
  **************************************************/

  //on tab change(since tab is a component)
  const handleTabChange = (newTab) => {
    setSelectedTab(newTab);
  };

  //on text field change
  const onFieldValueChange = (e) => {
    try {
      setFieldData(e.target.id, e.target.value);
    } catch (err) {
      alert(err);
    }
  };

  //updating form state data on any email change
  const setFieldData = (field, val) => {
    let _fieldData = { ...formStateData.formData };
    _fieldData[field].value = val;

    let _errorData = { ...formStateData.formError };
    if (_errorData[field]) {
      _errorData[field] = null;
    }

    const disableFormSubmit = !(
      !!_fieldData.UserEmail.value && !!_fieldData.UserRoleCode.value.length > 0
    );

    setFormStateData({
      ...formStateData,
      formData: _fieldData,
      formError: _errorData,
      disableFormSubmit
    });
  };


  //on user role change
  const onUserAccessCodeChange = (e) => {
    try {
      setUserAccessData(e.target.id, e.target.value, e.target.description);
    } catch (err) {
      alert(err);
    }
  };

  //updating form state data on any user role change
  const setUserAccessData = (field, val, description) => {
    let _fieldData = { ...formStateData.formData };
    _fieldData[field].value = val;
    if (description)
      _fieldData[field].description = description;
    setFormStateData({ ...formStateData, ...{ formData: _fieldData } });

    if (formStateData.formError[field]) {
      let _errorData = { ...formStateData.formError };
      _errorData[field] = null;
      setFormStateData({ ...formStateData, ...{ formError: _errorData } });
    }
    setFormStateData({
      ...formStateData,
      ...{
        disableFormSubmit: !(!!formStateData.formData.UserEmail.value && !!formStateData.formData.UserRoleCode.value.length > 0),
      },
    });
  };

  const validateEmail = (emailField) => {
    const isEmailValid = AppUtility.validateEmail(emailField);
    if (!isEmailValid)
      return 'Please enter a valid email address';
    if (user.userRecord.email === emailField)
      return 'Super admin cannot have other roles';
  }

  //on click of save button
  const handleSubmit = async (event) => {
    try {
      if (!setFormStateData.disableFormSubmit) {
        event.preventDefault();
        event.stopPropagation();

        // Email validation
        const emailField = formStateData.formData.UserEmail.value;
        const emailErrorMsg = validateEmail(emailField);


        if (emailErrorMsg) {
          const updatedFormError = {
            ...formStateData.formError,
            UserEmail: emailErrorMsg,
          };
          setFormStateData((prevData) => ({
            ...prevData,
            formError: updatedFormError,
          }));
          return;
        }

        await saveUserRole();
        setFormStateData({ ...formStateData, validated: true });
      }
    } catch (err) {
      alert(err);
    }
  };


  //on cancel of create screen
  const handleCancel = (e) => {
    try {
      setFormStateData({
        formData: _.cloneDeep(UserRoleFormData),
        formError: _.cloneDeep(UserRoleFormErrorData),
        disableFormSubmit: true,
        validated: false,
      });
      navigate("/userrolelist");
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Form Data & State handler
  **************************************************/

  const FormFieldContent = setUserRoleFormFieldContent(
    formStateData.formData,
    onFieldValueChange,
    onUserAccessCodeChange,
    formStateData.formError,
    EditUserEmail,
    formStateData,
    setFormStateData,
    t
  );

  //formatting payload
  const formAPIRequestData = () => {
    try {
      const userEmail = formStateData.formData.UserEmail.value;
      const userRoleIds = formStateData.formData.UserRoleCode.value;//user role Uid
      const userCodeDescriptions = formStateData.formData.UserRoleCode.description;// user role code like 1001
      const UserRoleMappingId = formStateData.formData.UserRoleCode.userRoleMappingId;//user role mapping Uid

      // Map over the userRole array
      const userRoleMappings = userRoleIds.map((id, index) => ({
        UserRoleMappingId: "",
        UserEmail: userEmail,
        UserRoleId: id,
        UserCode: userCodeDescriptions[index],
        Deleted: false,
      }));

      //deleting all old mappings
      const deleteOldMappings = UserRoleMappingId ? UserRoleMappingId.map((id) => ({
        UserRoleMappingId: id,
        UserEmail: userEmail,
        Deleted: true,
      })) : [];

      const combinedMappings = [
        ...userRoleMappings.map((mapping) => ({ data: mapping })),
        ...deleteOldMappings.map((mapping) => ({ data: mapping })),
      ];

      return combinedMappings
    } catch (e) {
      alert(e);
    }
  };

  //API call to save user role
  const saveUserRole = async () => {
    try {
      let _apiData = formAPIRequestData();

      let _postRequest = {
        countryCode: settingsConfig.countryCode,
        businessCode: settingsConfig.businessCode,
        userRoleMapping: []
      };
      _apiData.forEach((item) => {
        _postRequest.userRoleMapping.push({
          ...item.data,
        });
      });

      spinnerCtx.setLoadingIndicator(true);
      let res = await handlePostAction(_postRequest, "userrole");
      if (res && res.status && res.status === 200) {
        if (res.data && res.data.response && res.data.response.errorNumber) {
          const errorMessage = res.data.response.errorDescription || "User role creation failed";
          notifyToast(errorMessage, ToastType.ERROR);
          spinnerCtx.setLoadingIndicator(false);
        } else {
          notifyToast("User role added successfully", ToastType.SUCCESS);
          handleCancel();
        }
      } else {
        notifyToast("User role creation failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Form on load useEffect
  **************************************************/

  useEffect(() => {
    const initFormComponents = () => {
      if (EditUserEmail && navigationData) {
        let _formData = { ...formStateData.formData };

        const userRoleCodeValue = navigationData.roles.map(role => role.UserRoleId);
        const userRoleCodeDescription = navigationData.roles.map(role => role.UserRoleCode);
        const userRoleMappingId = navigationData.roles.map(role => role.UserRoleMappingId);
        _formData.UserRoleCode.value = userRoleCodeValue;
        _formData.UserRoleCode.description = userRoleCodeDescription;
        _formData.UserEmail.value = navigationData.UserEmail;
        _formData.UserRoleCode.userRoleMappingId = userRoleMappingId;

        setFormStateData((prevData) => ({
          ...prevData,
          formData: _formData,
        }));
      } else {
        setFormStateData({
          ...formStateData,
        });
      }
      spinnerCtx.setLoadingIndicator(false);
    };

    spinnerCtx.setLoadingIndicator(true);
    initFormComponents();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage>
      <TabPanel
        tabs={[
          {
            icon: "",
            label: t("createUserRole.tabHeader"),
            children: (
              <>
                <div style={{ fontWeight: "bold", height: "30px" }}>
                  {EditUserEmail ? t("createUserRole.editHeader") : t("createUserRole.createHeader")}
                </div>
                <AppDynamicForm formField={FormFieldContent} handleSubmit={handleSubmit} validated={formStateData.validated} />
                <FormButtonToolbar
                  onCancelClick={handleCancel}
                  onSubmitClick={handleSubmit}
                  isSubmitDisabled={formStateData.disableFormSubmit}
                  byPassRoleCheck={true}
                  buttonText={t("generic.save")}
                />
              </>
            ),
          },
        ]}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
      />
    </FormPage>
  );
};

export default CreateUserRoleForm;
